import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../store/useAppStore';
import { questions } from '../data/questions';
import styles from './Challenge.module.css';

const Challenge: React.FC = () => {
  const navigate = useNavigate();
  const wrongQuestions = useAppStore((s) => s.wrongQuestions);
  const startChallenge = useAppStore((s) => s.startChallenge);
  const completeChallenge = useAppStore((s) => s.completeChallenge);

  // 从未复习错题中抽取 5 题
  const challengeQuestionIds = useMemo(() => {
    const unreviewed = wrongQuestions.filter((w) => !w.retried);
    if (unreviewed.length === 0) return [];
    return startChallenge();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const challengeQuestions = useMemo(
    () => challengeQuestionIds.map((id) => questions.find((q) => q.id === id)).filter(Boolean) as typeof questions,
    [challengeQuestionIds]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [showExplanation, setShowExplanation] = useState(false);
  const [finished, setFinished] = useState(false);

  const currentQ = challengeQuestions[currentIndex];
  const isLast = currentIndex === challengeQuestions.length - 1;
  const isAnswered = !!answers[currentQ?.id];

  const handleSingleAnswer = (option: string) => {
    if (isAnswered) return;
    setAnswers({ ...answers, [currentQ.id]: option });
    setShowExplanation(false);
  };

  const handleMultiToggle = (option: string) => {
    const current = selectedOptions.includes(option)
      ? selectedOptions.filter((o) => o !== option)
      : [...selectedOptions, option];
    setSelectedOptions(current);
  };

  const handleMultiConfirm = () => {
    if (selectedOptions.length === 0) return;
    setAnswers({ ...answers, [currentQ.id]: selectedOptions });
    setShowExplanation(false);
  };

  const checkCorrect = (qId: string) => {
    const q = questions.find((x) => x.id === qId);
    if (!q) return false;
    const userAnswer = answers[qId];
    const correctAnswer = q.correctAnswer;
    if (Array.isArray(correctAnswer) && Array.isArray(userAnswer)) {
      return (
        correctAnswer.length === userAnswer.length &&
        correctAnswer.every((a) => userAnswer.includes(a))
      );
    }
    return userAnswer === correctAnswer;
  };

  const handleNext = () => {
    if (isLast) {
      handleFinish();
    } else {
      setCurrentIndex(currentIndex + 1);
      setSelectedOptions([]);
      setShowExplanation(false);
    }
  };

  const handleFinish = () => {
    let correctCount = 0;
    challengeQuestionIds.forEach((id) => {
      if (checkCorrect(id)) correctCount++;
    });
    completeChallenge(correctCount, challengeQuestionIds.length);
    setFinished(true);
  };

  // 空状态：没有待复习错题
  if (challengeQuestions.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyCard}>
          <div className={styles.emptyIcon}>🏆</div>
          <h2 className={styles.emptyTitle}>没有待复习的错题</h2>
          <p className={styles.emptySub}>
            你已经掌握了所有知识点，太棒了！
          </p>
          <button className={styles.backBtn} onClick={() => navigate('/review')}>
            返回复习中心
          </button>
        </div>
      </div>
    );
  }

  // 完成结果
  if (finished) {
    let correctCount = 0;
    challengeQuestionIds.forEach((id) => {
      if (checkCorrect(id)) correctCount++;
    });
    const total = challengeQuestionIds.length;
    const allPassed = correctCount === total;
    const rate = Math.round((correctCount / total) * 100);

    return (
      <div className={styles.container}>
        <div className={`${styles.resultCard} ${allPassed ? styles.resultPass : styles.resultFail}`}>
          <div className={styles.resultIcon}>{allPassed ? '🎉' : '💪'}</div>
          <h2 className={styles.resultTitle}>
            {allPassed ? '全部通过！' : '继续加油'}
          </h2>
          <div className={styles.scoreRow}>
            <span className={styles.scoreNum}>{correctCount}</span>
            <span className={styles.scoreSep}>/</span>
            <span className={styles.scoreTotal}>{total}</span>
          </div>
          <p className={styles.resultHint}>
            {allPassed
              ? '恭喜！你已经掌握了所有薄弱知识点。'
              : `正确率 ${rate}%，建议反复练习错题，巩固记忆。`}
          </p>
          <div className={styles.resultActions}>
            <button className={styles.retryBtn} onClick={() => {
              startChallenge();
              window.location.reload();
            }}>
              再来一次
            </button>
            <button className={styles.backReviewBtn} onClick={() => navigate('/review')}>
              返回复习中心
            </button>
          </div>
        </div>
      </div>
    );
  }

  const progressPercent = challengeQuestions.length > 0
    ? Math.round(((currentIndex + 1) / challengeQuestions.length) * 100)
    : 0;

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button className={styles.homeLink} onClick={() => navigate('/review')}>
          ← 返回复习
        </button>
        <span className={styles.modeLabel}>⚔️ 专项突破</span>
      </div>
      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className={styles.progressText}>
        题目 {currentIndex + 1} / {challengeQuestions.length}
      </div>

      {currentQ && (
        <div className={styles.questionCard} key={currentIndex}>
          <div className={styles.questionType}>
            {currentQ.type === 'single' && '单选题'}
            {currentQ.type === 'multiple' && '多选题'}
            {currentQ.type === 'truefalse' && '判断题'}
            {currentQ.type === 'scenario' && '情景题'}
          </div>

          <h2 className={styles.questionText}>{currentQ.text}</h2>

          <div className={styles.options}>
            {currentQ.options?.map((option, idx) => {
              const userAnswer = answers[currentQ.id];
              const correctAnswer = currentQ.correctAnswer;

              if (currentQ.type === 'multiple') {
                // 多选题：toggle 模式 + 勾选框
                const isSelected = selectedOptions.includes(option);
                let optionClass = styles.optionBtn;
                if (isAnswered) {
                  const isCorrectOption = Array.isArray(correctAnswer) && correctAnswer.includes(option);
                  if (isCorrectOption) optionClass += ` ${styles.optionCorrect}`;
                  else if (isSelected) optionClass += ` ${styles.optionWrong}`;
                  else optionClass += ` ${styles.optionDisabled}`;
                } else if (isSelected) {
                  optionClass += ` ${styles.optionSelected}`;
                }
                return (
                  <button
                    key={idx}
                    className={optionClass}
                    onClick={() => handleMultiToggle(option)}
                    disabled={isAnswered}
                  >
                    <span style={{ marginRight: 6, fontWeight: 700 }}>
                      {isSelected ? '☑' : '☐'}
                    </span>
                    {option}
                    {isAnswered && Array.isArray(correctAnswer) && correctAnswer.includes(option) && (
                      <span className={styles.optionMark}>✓</span>
                    )}
                    {isAnswered && isSelected && Array.isArray(correctAnswer) && !correctAnswer.includes(option) && (
                      <span className={styles.optionMark}>✗</span>
                    )}
                  </button>
                );
              }

              // 单选 / 判断 / 情景题：直接点击提交
              const isSelected = userAnswer === option;
              const isCorrectOption = correctAnswer === option;
              let optionClass = styles.optionBtn;
              if (isAnswered) {
                if (isCorrectOption) optionClass += ` ${styles.optionCorrect}`;
                else if (isSelected) optionClass += ` ${styles.optionWrong}`;
                else optionClass += ` ${styles.optionDisabled}`;
              } else if (isSelected) {
                optionClass += ` ${styles.optionSelected}`;
              }
              return (
                <button
                  key={idx}
                  className={optionClass}
                  onClick={() => handleSingleAnswer(option)}
                  disabled={isAnswered}
                >
                  <span className={styles.optionIdx}>{String.fromCharCode(65 + idx)}.</span>
                  {option}
                  {isAnswered && isCorrectOption && <span className={styles.optionMark}>✓</span>}
                  {isAnswered && isSelected && !isCorrectOption && <span className={styles.optionMark}>✗</span>}
                </button>
              );
            })}
          </div>

          {/* 多选题确认按钮 */}
          {currentQ.type === 'multiple' && !isAnswered && selectedOptions.length > 0 && (
            <button className={styles.submitBtn} onClick={handleMultiConfirm}>
              确认选择
            </button>
          )}

          {isAnswered && !showExplanation && (
            <div className={`${styles.feedback} ${checkCorrect(currentQ.id) ? styles.correct : styles.wrong}`}>
              <span className={styles.feedbackIcon}>
                {checkCorrect(currentQ.id) ? '✓' : '✗'}
              </span>
              <span>{checkCorrect(currentQ.id) ? '回答正确！' : '回答错误'}</span>
              <button className={styles.showExplainBtn} onClick={() => setShowExplanation(true)}>
                查看解释
              </button>
            </div>
          )}

          {showExplanation && (
            <div className={styles.explanation}>
              <h3>📖 解释</h3>
              <p>{currentQ.explanation}</p>
            </div>
          )}

          {isAnswered && (
            <button className={styles.submitBtn} onClick={handleNext}>
              {isLast ? '查看结果' : '下一题'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Challenge;
